import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Play, Pause, Music, TrendingUp, Clock, Disc } from "lucide-react";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RankingPage() {
  const [ranking, setRanking] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playingId: playing, handlePlay } = useAudioPlayer();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rankingRes, releasesRes] = await Promise.all([
          axios.get(`${API}/musics/ranking?limit=10`),
          axios.get(`${API}/musics/releases?limit=6`)
        ]);
        setRanking(rankingRes.data);
        setReleases(releasesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPlays = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#FFB800] flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white" data-testid="ranking-title">
                Top 10 Arrocha
              </h1>
              <p className="text-zinc-500">As músicas mais tocadas da semana</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ranking Principal */}
          <div className="lg:col-span-2">
            <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <Music className="text-[#FF007F]" size={20} />
                  Ranking Semanal
                </h2>
              </div>
              <div className="divide-y divide-white/5">
                {ranking.map((music, index) => (
                  <div
                    key={music.id}
                    className={`flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${playing === music.id ? 'bg-[#FF007F]/10' : ''}`}
                    data-testid={`ranking-item-${index}`}
                  >
                    {/* Position */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-lg ${
                      index < 3 
                        ? 'bg-gradient-to-br from-[#FFB800] to-[#FF007F] text-white' 
                        : 'bg-white/10 text-zinc-400'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Artist Image */}
                    <div className="relative">
                      {music.artist_image ? (
                        <img
                          src={music.artist_image}
                          alt={music.artist_name}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-zinc-800 flex items-center justify-center">
                          <Disc className="w-6 h-6 text-zinc-600" />
                        </div>
                      )}
                      {playing === music.id && (
                        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{music.title}</h3>
                      <p className="text-zinc-500 text-sm">{music.artist_name}</p>
                    </div>
                    
                    {/* Duration */}
                    <div className="hidden sm:flex items-center gap-1 text-zinc-500 text-sm">
                      <Clock size={14} />
                      {music.duration}
                    </div>
                    
                    {/* Plays */}
                    <div className="text-right">
                      <p className="text-white font-medium">{formatPlays(music.plays)}</p>
                      <p className="text-zinc-500 text-xs">plays</p>
                    </div>
                    
                    {/* Play Button */}
                    <button
                      onClick={() => { setRadioScope({ type: "all" }); handlePlay(music, ranking); }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 ${
                        playing === music.id ? 'bg-[#FF007F] animate-pulse' : 'bg-[#FF007F] hover:bg-[#FF3399]'
                      } ${!music.preview_url ? 'opacity-30 cursor-not-allowed' : ''}`}
                      data-testid={`play-btn-${index}`}
                      disabled={!music.preview_url}
                    >
                      {playing === music.id ? (
                        <Pause size={18} fill="white" className="text-white" />
                      ) : (
                        <Play size={18} fill="white" className="text-white ml-0.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Lançamentos */}
          <div>
            <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden sticky top-24">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <Disc className="text-[#00E5FF]" size={20} />
                  Lançamentos
                </h2>
              </div>
              <div className="divide-y divide-white/5">
                {releases.map((music, index) => (
                  <div
                    key={music.id}
                    className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                    data-testid={`release-item-${index}`}
                  >
                    {music.artist_image ? (
                      <img
                        src={music.artist_image}
                        alt={music.artist_name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <Disc className="w-5 h-5 text-zinc-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">{music.title}</h4>
                      <p className="text-zinc-500 text-xs">{music.artist_name}</p>
                    </div>
                    <span className="px-2 py-1 bg-[#00E5FF]/20 text-[#00E5FF] text-xs font-bold rounded-full">
                      NOVO
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
